import React, { useEffect, useMemo, useState } from 'react';
import { MagicBallIcon } from '../../../design-system/icons';
import { typography, textColors, surfaces, borders } from '../../../design-system/tokens';

/**
 * AIProcessingIndicator
 *
 * Small inline indicator shown inside the chat while AI operations are pending/executing.
 * - Shows the same MagicBallIcon used in empty-chat state
 * - Shows "thinking..." then after 2.5s switches to "building..."
 * - Animates dots: "." -> ".." -> "..." (loops)
 *
 * This component is intentionally self-contained to keep chat rendering logic clean.
 */
const AIProcessingIndicator = ({ className = '' }) => {
  const [phase, setPhase] = useState('thinking'); // 'thinking' -> 'building'
  const [dotCount, setDotCount] = useState(1);

  useEffect(() => {
    const dotsInterval = setInterval(() => {
      setDotCount((prev) => (prev >= 3 ? 1 : prev + 1));
    }, 500);

    const phaseTimeout = setTimeout(() => {
      setPhase('building');
    }, 2500);

    return () => {
      clearInterval(dotsInterval);
      clearTimeout(phaseTimeout);
    };
  }, []);

  const label = useMemo(() => {
    const dots = '.'.repeat(dotCount);
    return `${phase}${dots}`;
  }, [phase, dotCount]);

  return (
    <div className={`flex justify-start ${className}`}>
      <div className="max-w-[80%]">
        <div className={`rounded-lg p-3 ${surfaces.primary} border ${borders.default}`}>
          <div className="flex items-start gap-3">
            <div className="shrink-0 animate-bounce">
              <MagicBallIcon pixelSize={28} />
            </div>
            <div className="min-w-0">
              <div className={`${typography.caption} ${textColors.secondary}`}>
                {label}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIProcessingIndicator;


